import { Data, isDataLoaded, isDataError } from '../../types/data-providers';
import { TimelineEventDetailListItemValue } from '../TimelineEventDetailListItem/TimelineEventDetailListItem';
import { useTranslation } from 'react-i18next';
import { getPrecisionValue } from '../../lib/precision-helper';
import { DevicePropertiesDetails, DeviceChannelData } from '../../types/device';

// @ts-ignore
type Translation = ReturnType<typeof useTranslation>['t'];

export function getDeviceName(deviceData: Data<{
    deviceProperties: Data<DevicePropertiesDetails>;
    channelData: Data<DeviceChannelData[]>;
}>,
    t: Translation): TimelineEventDetailListItemValue[] {
    return [
        {
            itemType: 'DeviceDetail',
            name: 'Device Name',
            value: isDataLoaded(deviceData) && isDataLoaded(deviceData.value.deviceProperties)
                ? deviceData.value.deviceProperties.value.name || t('EVENT_DETAILS.EMPTY_VALUE')
                : t('EVENT_DETAILS.UNKNOWN'),
        },
    ];
}
export const getAlarmStatus = (args: {
    trigger: string;
    severity: string;
    channelName: string | null;
    occuredValue: string | number | undefined;
    unit: string;
}): TimelineEventDetailListItemValue[] => [
    {
        itemType: 'AlarmStatus',
        trigger: args.trigger,
        severity: args.severity,
        channelName: args.channelName || '',
        occuredValue: args.occuredValue ? getPrecisionValue(args.occuredValue.toString()) : '',
        unit: args.unit,
    },
];

export const getLastDataReceived = (
    args: {
        latestTime: string | null;
        status: string;
    } | null
): TimelineEventDetailListItemValue[] => {
    if (args !== null) {
        return [
            {
                itemType: 'LastDataReceived',
                latestTime: args.latestTime,
                status: args.status,
            },
        ];
    }

    return [
        {
            itemType: 'LastDataReceived',
            latestTime: '',
            status: '',
        },
    ];
};
export const getTimeAlarmSent = (args: { occuredTime: string }): TimelineEventDetailListItemValue[] => [
    {
        itemType: 'TimeAlarmSent',
        occuredTime: args.occuredTime,
    },
];

export const getLastReading = (args: {
    threshold: string | number;
    unit: string;
}): TimelineEventDetailListItemValue[] => [
    {
        itemType: 'SetPoint',
        threshold: getPrecisionValue(args.threshold.toString()),
        unit: args.unit,
    },
];

export const getChannelData = (
    args: Array<{ id: number | string; name: string | null; value: string | null; unit: string | null }>
): TimelineEventDetailListItemValue[] =>
    args.map((channelDatum) => ({
        itemType: 'ChannelDatum',
        channelName: channelDatum.name,
        value: getPrecisionValue(channelDatum.value),
        units: channelDatum.unit,
    }));

export const getDeviceDetails = (
    deviceData: Data<{
        deviceProperties: Data<DevicePropertiesDetails>;
        channelData: Data<DeviceChannelData[]>;
    }>,
    t: Translation
): TimelineEventDetailListItemValue[] =>
    isDataLoaded(deviceData) && isDataLoaded(deviceData.value.deviceProperties)
        ? [
              {
                  itemType: 'DeviceDetail',
                  name: t('EVENT_DETAILS.DEVICE_MODEL'),
                  value: deviceData.value.deviceProperties.value
                      ? deviceData.value.deviceProperties.value.family || t('EVENT_DETAILS.EMPTY_VALUE')
                      : t('EVENT_DETAILS.UNKNOWN'),
              },
              {
                  itemType: 'DeviceDetail',
                  name: t('EVENT_DETAILS.DEVICE_SERIAL'),
                  value: deviceData.value.deviceProperties
                      ? deviceData.value.deviceProperties.value.serialNumber || t('EVENT_DETAILS.EMPTY_VALUE')
                      : t('EVENT_DETAILS.UNKNOWN'),
              },
          ]
        : isDataError(deviceData)
        ? [
              {
                  itemType: 'Error',
                  message: t('EVENT_DETAILS.ERROR_LOADING_DEVICE_DETAILS'),
              },
          ]
        : [{ itemType: 'NoData', message: t('EVENT_DETAILS.NO_DEVICE_DETAILS'), icon: 'Device' }];
